import React, {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
} from 'react';
import styled from 'styled-components';
import {
  BorderProps,
  ColorProps,
  HeightProps,
  SpaceProps,
  TypographyProps,
  WidthProps,
  borderColor as ssBorderColor,
  borderRadius as ssBorderRadius,
  borders as ssBorders,
  color as ssColor,
  fontSize as ssFontSize,
  height as ssHeight,
  space as ssSpace,
  width as ssWidth,
} from 'styled-system';

import { Box } from 'components/Box';
import { AnimatedCheckmark } from 'components/Icon/Icon';
import { BodySmall } from 'components/Text';
import { colors, transition } from 'theme';

const Prefix = styled(Box)`
  cursor: pointer;
`;

/**
 * This input should no longer be used because:
 *
 * 1. It has two completely different implementations based on `prefix` being provided.
 * 2. It can include a `successMessage` that is not wrapped in a container.
 * 3. Other components such as `SearchInput` mutate the internal styles, making it difficult to make changes.
 * 4. It accepts too many mutating properties such as color and padding
 * 5. It's not easily extensible - it works well for what's included, but it's hard to, for example, add a new style of prefix
 *
 * Due to this component being used in multiple places it was renamed and not
 * removed entirely, if you're looking for an input please refer to `Input`,
 * it provides the same functionality with a different implementation.
 */
export const LegacyInput = styled(
  React.forwardRef(
    (
      {
        changeHandler,
        onChange: onChangeProp,
        onUpdateValue,
        className,
        id,
        mt,
        mb,
        ml,
        mr,
        mx,
        my,
        pt,
        pb,
        pl,
        pr,
        px,
        py,
        color,
        height,
        width,
        fontSize,
        fontWeight,
        border,
        borderLeft,
        borderRight,
        borderTop,
        borderBottom,
        borderColor,
        borderRadius,
        hasError,
        hasSuccess,
        isValid,
        prefix,
        ...otherProps
      }: ColorProps &
        WidthProps &
        HeightProps &
        TypographyProps &
        SpaceProps &
        BorderProps & {
          name?: string;
          type?: string;
          label?: string;
          autoFocus?: boolean;
          changeHandler?: ChangeEventHandler<HTMLInputElement>;
          onChange?: ChangeEventHandler<HTMLInputElement>;
          onFocus?: FocusEventHandler;
          onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
          onKeyPress?: KeyboardEventHandler;
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onUpdateValue?: (...args: Array<any>) => any;
          className?: string;
          placeholder?: string;
          id?: string;
          hasError?: boolean;
          hasSuccess?: boolean;
          isValid?: boolean;
          prefix?: string;
          value?: string | number;
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          defaultValue?: any;
          min?: number | string;
          max?: number | string;
          'data-qa-id'?: string;
        },
      ref
    ) => {
      const onChange = changeHandler
        ? (e) => {
            changeHandler(e.target.value);
          }
        : onChangeProp;
      return prefix ? (
        <Box
          className={className}
          position="relative"
          display="flex"
          flexDirection="row-reverse"
        >
          {/* TODO: Fix this the next time the file is edited. */}
          {/* eslint-disable-next-line react/jsx-props-no-spreading, @typescript-eslint/no-explicit-any */}
          <input ref={ref as any} {...otherProps} onChange={onChange} id={id} />
          <Prefix
            component="label"
            htmlFor={id}
            flex="0 0 auto"
            px="8px"
            display="flex"
            alignItems="center"
            color={colors.battleshipGrey}
            bg={colors.aluminum}
            border={`1px solid ${colors.silver}`}
            borderRight="0"
            borderRadius="4px 0 0 4px"
            transition={`all ${transition}`}
          >
            <BodySmall styleAs="h5">{prefix}</BodySmall>
          </Prefix>
        </Box>
      ) : (
        <>
          <input
            // @ts-expect-error ts-migrate(2322) FIXME: Type '((instance: unknown) => void) | MutableRefOb... Remove this comment to see the full error message
            ref={ref}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...otherProps}
            onChange={onChange}
            id={id}
            className={className}
          />
          {hasSuccess && (
            <Box
              position="absolute"
              right="8px"
              top="50%"
              transform="translatey(-50%)"
              display="flex"
              width={24}
              height={24}
              alignItems="center"
              justifyContent="center"
              borderRadius="50%"
              bg={colors.turtleGreen}
            >
              <AnimatedCheckmark stroke="white" />
            </Box>
          )}
        </>
      );
    }
  )
)`
  ${(props) => (props.prefix ? 'input' : '&')} {
    margin: 0;
    padding: 11px 1em;
    font: inherit;
    font-size: 18px;
    color: ${colors.charcoalGrey};
    border-radius: ${(props) => (props.prefix ? '0 4px 4px 0' : '4px')};
    border: solid 1px;
    border-color: ${colors.silver};
    height: 45px;
    width: 100%;
    background-color: ${colors.white};
    appearance: none;
    transition: all ${transition};
    ::placeholder {
      color: ${colors.middleGrey};
    }
    &:focus {
      outline: 0;
    }
    ${(props) =>
      props.isValid
        ? `
    border-color: ${colors.turtleGreen};
    box-shadow: 0 0 0 1px ${colors.turtleGreen};
        `
        : `
    &:focus {
      border-color: ${colors.waterBlue};
      box-shadow: 0 0 0 1px ${colors.waterBlue};
      & + ${Prefix} {
        background: ${colors.waterBlue};
        box-shadow: 0 0 0 1px ${colors.waterBlue};
        border-color: ${colors.waterBlue};
        color: white;
      }
    }`}
    ${(props) =>
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-nested-ternary
      props.hasError
        ? `
      border-color: ${colors.scarlet};
      box-shadow: 0 0 0 1px ${colors.scarlet};
    `
        : props.hasSuccess
        ? `
      border-color: ${colors.turtleGreen};
      box-shadow: 0 0 0 1px ${colors.turtleGreen};
    `
        : ''}
    &:disabled {
      background-color: ${colors.lightGrey};
    }
    ${ssWidth} ${ssHeight} ${ssColor} ${ssSpace} ${ssFontSize}
    ${ssBorders} ${ssBorderRadius} ${ssBorderColor};
  }
`;
